import { Link } from "wouter";
import {
  Target, Eye, ArrowRight, Check, ChevronRight, MapPin,
} from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import {
  COMPANY_SNAPSHOT,
  ABOUT_PROBLEMS,
  ABOUT_DIFFERENTIATORS,
  ABOUT_PRINCIPLES,
  PRODUCT_JOURNEY,
  ABOUT_JOURNEY_LINKS,
  PRODUCT_FACTS,
  INDIA_COMPLIANCE,
} from "../lib/content";

export default function About() {
  useSEO({
    title: "About Alea ERP — Why We Built an Integrated Business ERP",
    description:
      "Alea ERP brings people, payroll, finance, procurement and operations together in one secure platform — so businesses work from a single source of truth. Based in Jaipur, built for Indian and growing enterprises.",
    path: "/about",
  });

  return (
    <MarketingLayout>
      {/* ───────────────── Hero ───────────────── */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-32 pb-20 lg:pt-40">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 h-96 w-full max-w-[700px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]"
        />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-300">
              About Alea ERP
            </span>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              The system of record for modern businesses.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Alea ERP brings people, payroll, finance, procurement and operations
              together in one secure platform, so businesses can work from a single
              source of truth — instead of a tangle of disconnected tools.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── Company snapshot ───────────────── */}
      <section className="border-y border-slate-100 bg-white py-12" aria-label="Company snapshot">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {COMPANY_SNAPSHOT.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.05} className="text-center">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {item.label}
                </div>
                <div className="mt-2 text-base font-bold text-[#0a2d4a] sm:text-lg">
                  {item.value}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Mission / Vision ───────────────── */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-2">
          {[
            {
              icon: Target,
              label: "Our Mission",
              headline: "Make complex business operations easier to run.",
              text: "Alea connects the people, processes and financial operations behind a business so teams can spend less time managing disconnected systems and more time running the business.",
            },
            {
              icon: Eye,
              label: "Our Vision",
              headline: "One connected operating system for the modern business.",
              text: "From employee management and payroll to finance, procurement and reporting, important business processes should work from connected data.",
            },
          ].map((item, i) => (
            <Reveal key={item.label} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-slate-200 bg-slate-50/60 p-8">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <item.icon aria-hidden="true" className="h-6 w-6" />
                </div>
                <h2 className="mt-5 text-sm font-semibold uppercase tracking-wider text-blue-600">
                  {item.label}
                </h2>
                <p className="mt-2 text-xl font-bold text-[#0a2d4a]">{item.headline}</p>
                <p className="mt-3 leading-relaxed text-slate-600">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ───────────────── Problems → Solutions ───────────────── */}
      <section className="bg-slate-50 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {/*
            Desktop: image left | heading + cards right
            Mobile:  heading → image → cards (via `contents` so children share grid order)
          */}
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
            <Reveal className="order-2 lg:order-1">
              <img
                src={`${import.meta.env.BASE_URL.replace(/\/$/, "")}/about_us_image.png`}
                alt="Alea ERP connects HR, payroll, finance, procurement, workflows and reporting in one integrated platform."
                width={799}
                height={1024}
                loading="lazy"
                decoding="async"
                className="mx-auto h-auto w-full max-w-md object-contain sm:max-w-lg lg:max-w-none"
              />
            </Reveal>

            <div className="contents lg:order-2 lg:flex lg:flex-col lg:gap-6">
              <Reveal className="order-1">
                <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                  Why we built this
                </span>
                <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
                  The problems we set out to solve
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-slate-600">
                  Capable teams lose hours every week to tools that don&apos;t talk to
                  each other. Alea ERP is our answer: one platform, one truth, zero silos.
                </p>
              </Reveal>

              <div className="order-3 space-y-4">
                {ABOUT_PROBLEMS.map((item, i) => (
                  <Reveal key={item.problem} delay={i * 0.06}>
                    <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-500">
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                            Problem
                          </p>
                          <p className="mt-1 text-sm font-medium text-slate-700">{item.problem}</p>
                          <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5">
                            <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                              Alea approach
                            </p>
                            <p className="mt-1 flex items-start gap-2 text-sm font-medium text-[#0a2d4a]">
                              <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                              {item.solution}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── What makes Alea different ───────────────── */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Differentiation
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              What makes Alea different?
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2">
            {ABOUT_DIFFERENTIATORS.map((d, i) => (
              <Reveal key={d.title} delay={(i % 2) * 0.08}>
                <div className="flex h-full gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-6">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <d.icon aria-hidden="true" className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#0a2d4a]">{d.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{d.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Principles ───────────────── */}
      <section className="bg-slate-50 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Our principles
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              The principles behind every release
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT_PRINCIPLES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <v.icon aria-hidden="true" className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base font-bold text-[#0a2d4a]">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{v.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Product journey (not fabricated history) ───────────────── */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              How we build
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              How Alea is building the platform
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
              A product journey — how we approach building a connected ERP — not a
              fictional company timeline.
            </p>
          </Reveal>
          <div className="mt-14 space-y-8">
            {PRODUCT_JOURNEY.map((t, i) => (
              <Reveal key={t.step} delay={i * 0.08}>
                <div className="flex gap-6">
                  <div className="flex flex-col items-center">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#0a2d4a] text-sm font-bold text-white">
                      {t.step}
                    </div>
                    {i < PRODUCT_JOURNEY.length - 1 && (
                      <div aria-hidden="true" className="mt-2 w-px flex-1 bg-slate-300" />
                    )}
                  </div>
                  <div className="pb-2 pt-3">
                    <h3 className="text-lg font-bold text-[#0a2d4a]">{t.title}</h3>
                    <p className="mt-1 leading-relaxed text-slate-600">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Product proof ───────────────── */}
      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Product proof
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              A real operating ERP platform
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Credibility from verified product capabilities — not invented customer
              logos or unverifiable statistics.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-y-8 gap-x-6 sm:grid-cols-4">
            {PRODUCT_FACTS.map((fact, i) => (
              <Reveal key={fact.label} delay={i * 0.06} className="flex flex-col items-center gap-2.5 text-center">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <fact.icon aria-hidden="true" className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold text-[#0a2d4a]">{fact.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── India-ready ───────────────── */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                Local by design
              </span>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
                Built for the realities of Indian business
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Payroll and finance include Indian statutory capabilities from the
                start — so compliance is part of the product, not a spreadsheet afterthought.
              </p>
              <p className="mt-4 flex items-start gap-2 text-sm text-slate-500">
                <MapPin aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                Team based in Jaipur, Rajasthan — serving Indian and growing businesses.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {INDIA_COMPLIANCE.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.05}>
                  <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                    <item.icon aria-hidden="true" className="h-4 w-4 shrink-0 text-blue-600" />
                    <span className="text-xs font-semibold text-[#0a2d4a]">{item.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Connect to product journey ───────────────── */}
      <section className="border-y border-slate-100 bg-slate-50 py-14">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center">
            <h2 className="text-xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-2xl">
              Continue exploring Alea
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              About explains why we exist. These pages show what Alea does next.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {ABOUT_JOURNEY_LINKS.map((link, i) => (
              <Reveal key={link.href} delay={i * 0.05}>
                <Link
                  href={link.href}
                  className="group flex flex-col rounded-xl border border-slate-200 bg-white px-5 py-4 transition-colors hover:border-blue-200 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0a2d4a]">
                    {link.label}
                    <ChevronRight
                      aria-hidden="true"
                      className="h-4 w-4 text-blue-600 transition-transform group-hover:translate-x-0.5"
                    />
                  </span>
                  <span className="mt-1 text-xs text-slate-500">{link.blurb}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Final CTA ───────────────── */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a2d4a] to-[#0d3a5f] px-8 py-14 text-center shadow-2xl sm:px-16 sm:py-16">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl"
              />
              <h2 className="relative text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                See Alea ERP in action
              </h2>
              <p className="relative mx-auto mt-4 max-w-xl text-lg text-slate-300">
                Explore how HR, payroll, finance, procurement and operations work
                together in one platform.
              </p>
              <div className="relative mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                <Link
                  href="/demo-access"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  Explore ERP
                  <ArrowRight
                    aria-hidden="true"
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  Book a Demo
                </Link>
                <Link
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
}
